import { FC, useEffect, useState } from "react";
import { Button, Space } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { Vendor } from "../../../../types/VendorTypes";
import { getVendors, loadVendorSuggestions } from "../../../../common/api/VendorClient";
import { VendorInput } from "../../../vendor/VendorInput";
import { CompanyImage } from "../../../../common/components/CompanyImage";

interface RfxVendorPickerProps {
    value: string[]; // vendorKeys
    onChange: (value: string[]) => void;
}

/**
 * Admin multi-picker for RFx candidate vendors. Reuses the same `VendorInput`
 * widget as the Deal tab for add — picked vendors are displayed with their
 * name and logo, and can be removed individually.
 */
export const RfxVendorPicker: FC<RfxVendorPickerProps> = ({ value, onChange }) => {
    const selectedKeys = value;

    // Resolve vendor metadata for already-selected keys so we can show names/logos.
    const [resolved, setResolved] = useState<Map<string, Vendor>>(new Map());
    useEffect(() => {
        if (selectedKeys.length === 0) {
            setResolved(new Map());
            return;
        }
        const missing = selectedKeys.filter((k) => !resolved.has(k));
        if (missing.length === 0) return;
        loadVendorSuggestions("")
            .then((all) => {
                const byKey = new Map<string, Vendor>(resolved);
                for (const v of all) {
                    if (selectedKeys.includes(v.vendorKey) && !byKey.has(v.vendorKey)) {
                        byKey.set(v.vendorKey, v);
                    }
                }
                setResolved(byKey);
            })
            .catch(() => {});
    }, [selectedKeys.join(",")]);

    // Rekey VendorInput after each add so it resets to an empty state.
    const [inputNonce, setInputNonce] = useState(0);

    const add = async (vendorKey: string) => {
        if (!vendorKey || selectedKeys.includes(vendorKey)) return;
        const [vendor] = await getVendors([vendorKey]).catch(() => []);
        if (!vendor) return;
        onChange([...selectedKeys, vendor.vendorKey]);
        setResolved((r) => new Map(r).set(vendor.vendorKey, vendor));
        setInputNonce((n) => n + 1);
    };

    const remove = (vendorKey: string) => {
        onChange(selectedKeys.filter((k) => k !== vendorKey));
    };

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            {selectedKeys.map((k) => {
                const v = resolved.get(k);
                return (
                    <div
                        key={k}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                    >
                        <CompanyImage logo={v?.hero} size="xs" name={v?.name ?? ""} />
                        <span style={{ flex: 1 }}>{v?.name ?? k}</span>
                        <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(k)}
                        />
                    </div>
                );
            })}
            <VendorInput key={inputNonce} onSelect={add} excludeKeys={selectedKeys} />
        </Space>
    );
};
